/**
 * @file Typed read path for player-facing pack listing. Extracted from `packs.ts`
 * (which is `@ts-nocheck`) so the hot-path read used in pregame is fully type-checked.
 * Add other migrated pack functions here as they leave `packs.ts`.
 */

import { collection, onSnapshot, query, where, type DocumentData } from 'firebase/firestore'
import { db } from '@/main'
import type { Pack } from '@/types/Types'

function toPack(id: string, data: DocumentData): Pack {
  return {
    id,
    order: data.order,
    challenges: data.challenges,
    title: data.title,
    description: data.description,
    difficulty: data.difficulty,
    type: data.type,
    image: data.image,
    combinable: data.combinable,
    palette: data.palette,
    createdAt: data.createdAt,
    updatedAt: data.updatedAt,
    isPublished: data.isPublished
  }
}

/**
 * Player-facing pack list — only documents with `isPublished === true`. Errors are swallowed
 * to an empty array (rather than rejecting) so the pregame view never crashes on a partial
 * Firestore outage; the UI shows "no packs available" instead. Does not write to offline cache.
 */
export function getPublishedPacks(): Promise<Pack[]> {
  return new Promise((resolve) => {
    const fetchedPacks: Pack[] = []
    try {
      const unsub = onSnapshot(
        query(collection(db, 'packs'), where('isPublished', '==', true)),
        (querySnapshot) => {
          querySnapshot.forEach((doc) => {
            fetchedPacks.push(toPack(doc.id, doc.data()))
          })
          unsub()
          resolve(fetchedPacks)
        },
        (error) => {
          console.error('Error fetching published packs:', error)
          resolve([])
        }
      )
    } catch (error) {
      console.error('Error setting up published packs query:', error)
      resolve([])
    }
  })
}
